import { v4 as uuidv4 } from "uuid";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { checkPromptSafety } from "./shared/check-prompt-safety";
import { fetchInferenceJob } from "./shared/fetch-inference-job";
import { getTargetEditorImage } from "./shared/target-editor-image";

const callSeedreamEditing = async (
	prompt: string,
	size: string,
	model: string,
	input_images: string[],
	messageId: string,
	jwt: string,
	tool_document_id: string,
	batch_size: number,
) => {
	const promptSafetyResult = checkPromptSafety(prompt);
	if (promptSafetyResult.detected) {
		await promptSafetyResult.onFailed();
		return false;
	}

	const { success } = await getTargetEditorImage({
		commandName: "Seedream - Image Editing",
		resultLayerName: "Seedream - Image Editing",
		includeMask: true,
		applyMaskToResultLayer: true,
		placeAsSmartObject: true,
		maxSide: undefined,
		processImage: async ({ executionContext, imageBase64 }) => {
			const data = {
				prompt: prompt,
				model,
				input_images: [imageBase64, ...input_images],
				from_editor: true,
				resolution: size,
				jwt,
				tool_document_id,
				batch_size,
			};

			return fetchSeedreamEditing(data, executionContext, messageId);
		},
	});

	if (success) {
		const doc = photoshop.app.activeDocument;
		if (doc) {
			postToRenderer([
				{
					key: "documentInfoChanged",
					id: uuidv4(),
					values: {
						width: doc.width,
						height: doc.height,
					},
				},
			]);
		}
	}

	return success;
};

export async function fetchSeedreamEditing(
	params: {
		prompt: string;
		model: string;
		input_images: string[];
		from_editor: boolean;
		resolution: string;
		jwt: string;
		tool_document_id: string;
		batch_size: number;
	},
	executionContext: any,
	messageId: string,
): Promise<string | string[] | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath: "/infer/seedream",
		statusEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/seedream", "")}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/seedream", "")}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl.replace("/infer/seedream", "")}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult: (data) => data.image_base64_list ?? [data.image_base64],
		useGatewayUrl: true,
	});
}

export function useListenSeedreamEditingMessage() {
	useListenFromRenderer([
		{
			key: "seedreamEditing",
			onMessage: async (message) => {
				const success = await callSeedreamEditing(
					message.values.prompt,
					message.values.size,
					message.values.model,
					message.values.input_images,
					message.id,
					message.values.jwt,
					message.values.tool_document_id,
					message.values.batch_size,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}

